import { Intent, ToastProps } from '@blueprintjs/core';
import { IconName, IconNames } from '@blueprintjs/icons';
import omit from 'lodash/omit';
import { TrackingEventList } from 'portal-sdk';
import { isActionSynced } from 'redux-state-sync';
import { v4 as uuid } from 'uuid';
import { isEqual } from 'vet-bones/utils';
import { isOnPremise, sendHubspotEvent } from 'vet-bones/utils/portal';

import type { DataSourcesAction } from 'src/actions/data-sources/actionCreators';
import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import type { DatabasesAction } from 'src/actions/databases/actionCreators';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import {
  NotificationsAction,
  NotificationsActionType,
} from 'src/actions/notifications/actionCreators';
import type { ProjectsAction } from 'src/actions/projects/actionCreators';
import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import { FailureResponse } from 'src/actions/request/types';
import { getNotificationActionButtonTestId } from 'src/constants/testIds';
import { AnyObj, UnknownAction } from 'src/constants/types';
import { connectionAsyncActions } from 'src/store/connections/connections.slice';
import {
  NotificationAction,
  NotificationMetadata,
  NotificationsState,
} from 'src/store/state/notifications';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';

const queueNotification = (
  prevState: NotificationsState,
  type: string,
  notification: ToastProps,
  id: string = uuid(),
  data: NotificationMetadata = {}
): NotificationsState => {
  const isFailure = notification.intent === Intent.DANGER;
  const isWarning = notification.intent === Intent.WARNING;

  if (isFailure) {
    console.error(notification.message);
  }

  if (isWarning) {
    console.warn(notification.message);
  }

  let timeout = 10000;
  if (notification.timeout !== undefined) {
    timeout = notification.timeout;
  } else if (isFailure) {
    timeout = 0;
  } else if (isWarning) {
    timeout = 30000;
  }
  const notifications = [...prevState.notifications];

  const prevNotification = notifications[notifications.length - 1];
  const nextNotification = {
    ...notification,
    id,
    type,
    data,
    timeout,
  };

  if (isEqual(omit(prevNotification, 'id'), omit(nextNotification, 'id'))) {
    notifications.splice(notifications.length - 1, 1, nextNotification);
  } else {
    notifications.push(nextNotification);
  }

  return {
    ...prevState,
    notifications,
  };
};

export const buildStardogFailureMessage = <T extends AnyObj, A extends string>(
  action: FailureResponse<T, A>
): string => {
  const errorMessage = action.payload?.response?.errorMessage;
  if (errorMessage) {
    return errorMessage;
  }

  const failureCode = action.payload?.response?.body?.code;
  const failureMessage = action.payload?.response?.body?.message;
  const statusText = action.payload?.response?.statusText;

  let message: string;
  if (failureCode) {
    if (failureMessage) {
      message = statusText
        ? `${failureCode}: ${failureMessage} (${statusText})`
        : `${failureCode}: ${failureMessage}`;
    } else {
      message = statusText
        ? `${statusText} (Code: ${failureCode})`
        : `(Code: ${failureCode})`;
    }
  } else if (failureMessage) {
    message = statusText ? `${failureMessage} (${statusText})` : failureMessage;
  } else {
    message = statusText;
  }

  return message || copy.notifications.defaults.failure;
};

const queueFailureNotification = <T extends AnyObj, A extends string>(
  prevState: NotificationsState,
  action: FailureResponse<T, A>,
  actionDescription: string,
  message: string
) => {
  let errorMessage: string;
  let errorIcon: IconName;

  const responseStatus = action.payload?.response?.status;
  if (responseStatus === 403) {
    errorMessage = copy.notifications.defaults.forbidden(actionDescription);
    errorIcon = IconNames.LOCK;
  } else {
    errorMessage = buildStardogFailureMessage(action);
    errorIcon = IconNames.ERROR;
  }

  return queueNotification(prevState, action.type, {
    message: `${message}: ${errorMessage}`,
    icon: errorIcon,
    intent: Intent.DANGER,
  });
};

const queueConnectionFailureNotification = <T extends AnyObj, A extends string>(
  prevState: NotificationsState,
  action: FailureResponse<T, A>
) => {
  let errorMessage: string;
  let errorIcon: IconName;

  switch (buildStardogFailureMessage(action)) {
    case 'Network request failed':
    case 'net::ERR_CONNECTION_REFUSED':
      errorMessage = copy.notifications.validateConnection.failure.refused;
      errorIcon = IconNames.BAN_CIRCLE;
      break;
    case 'DISCONNECTED':
      errorMessage = copy.notifications.validateConnection.failure.disconnected;
      errorIcon = icons.connection;
      break;
    case 'Unauthorized':
    case 'Invalid Credentials':
    case '401':
      errorMessage = copy.notifications.validateConnection.failure.invalid;
      errorIcon = IconNames.LOCK;
      break;
    case 'Request Aborted':
      errorMessage = copy.notifications.validateConnection.failure.aborted;
      errorIcon = IconNames.BAN_CIRCLE;
      break;
    default:
      errorMessage = copy.notifications.validateConnection.failure.default;
      errorIcon = IconNames.ERROR;
  }

  return queueNotification(prevState, action.type, {
    message: errorMessage,
    icon: errorIcon,
    intent: Intent.DANGER,
  });
};

// TODO: refactor reducer to fix slice types
type ActionsToFix =
  | UnknownAction<'connections/requestValidateConnection/fulfilled'>
  | UnknownAction<'connections/requestValidateConnection/rejected'>
  | UnknownAction<'ui/voiceboxFeedbackFailed'>
  | UnknownAction<'ui/voiceboxModelingAndMappingFailed'>;

export const notificationsReducer = (
  prevState: NotificationsState = new NotificationsState(),
  action:
    | NotificationsAction
    | DatabasesAction
    | ProjectsAction
    | DataSourcesAction
    | ActionsToFix
): NotificationsState => {
  if (isActionSynced(action)) {
    // Only show notifications in originating tab, not in synced tabs.
    return prevState;
  }

  switch (action.type) {
    case NotificationsActionType.DISMISSED_NOTIFICATION:
      return {
        ...prevState,
        notifications: prevState.notifications.filter(
          ({ id }) => id !== action.payload.notificationId
        ),
      };

    case NotificationsActionType.MAX_FILE_SIZE_EXCEEDED: {
      return queueNotification(prevState, action.type, {
        message: copy.notifications.defaults.maxFileSizeExceeded,
        intent: Intent.WARNING,
        icon: IconNames.ERROR,
      });
    }

    case ProjectsActionType.REFRESH_PROJECT_MODEL_FAILED: {
      return queueNotification(prevState, action.type, {
        message: copy.notifications.defaults.refreshProjectModelFailed,
        intent: Intent.DANGER,
        icon: IconNames.ERROR,
      });
    }

    case ProjectsActionType.REFRESH_PROJECT_MODEL_NO_CHANGES: {
      return queueNotification(prevState, action.type, {
        message: copy.notifications.defaults.refreshProjectModelNoChanges,
        intent: Intent.SUCCESS,
        icon: IconNames.UPDATED,
      });
    }

    case NotificationsActionType.VOICEBOX_GENERATE_QUERIES_FAILED:
      return queueNotification(prevState, action.type, {
        message:
          action.payload.questionIds.length > 1
            ? copy.notifications.voicebox.generateQueries
            : copy.notifications.voicebox.generateQuery,
        intent: Intent.WARNING,
        icon: IconNames.ERROR,
      });
    case NotificationsActionType.VOICEBOX_GENERATE_QUESTIONS_FAILED:
      return queueNotification(prevState, action.type, {
        message: copy.notifications.voicebox.generateQuestions,
        intent: Intent.DANGER,
        icon: IconNames.ERROR,
      });
    case NotificationsActionType.VOICEBOX_GENERATE_SCORES_FAILED:
      return queueNotification(prevState, action.type, {
        message:
          action.payload.questionIds.length > 1
            ? copy.notifications.voicebox.generateScores
            : copy.notifications.voicebox.generateScore,
        intent: Intent.DANGER,
        icon: IconNames.ERROR,
      });
    case NotificationsActionType.VOICEBOX_GENERATE_SUMMARY_FAILED:
      return queueNotification(prevState, action.type, {
        message: copy.notifications.voicebox.generateSummary,
        intent: Intent.DANGER,
        icon: IconNames.ERROR,
      });

    case NotificationsActionType.VOICEBOX_SUGGESTIONS_FAILED:
      return queueNotification(prevState, action.type, {
        message: copy.notifications.voicebox.generateMappings,
        intent: Intent.DANGER,
        icon: IconNames.ERROR,
      });

    // TODO: refactor reducer to fix connection action types
    case connectionAsyncActions.requestValidateConnection.rejected.type:
      return queueConnectionFailureNotification(prevState, action as any);
    case connectionAsyncActions.requestValidateConnection.fulfilled.type: {
      if (action.meta.arg.shouldNotNotifySuccess) {
        return prevState;
      }

      return queueNotification(prevState, action.type as string, {
        message: copy.notifications.validateConnection.success,
        intent: Intent.SUCCESS,
        icon: icons.connection,
      });
    }

    case DatabasesActionType.ADD_MODEL_FILE_TO_DATABASE_FINISHED: {
      const { intent } = action.payload;

      if (intent === Intent.DANGER) {
        return queueNotification(prevState, action.type as string, {
          message: copy.notifications.database.addModelFile.failure,
          intent: Intent.DANGER,
          icon: icons.database,
        });
      }

      return queueNotification(prevState, action.type as string, {
        message: copy.notifications.database.addModelFile.success,
        intent: Intent.SUCCESS,
        icon: icons.database,
      });
    }

    case DataSourcesActionType.ADD_DATA_SOURCE_FAILURE: {
      return queueFailureNotification(
        prevState,
        action,
        copy.notifications.dataSources.creation.actionDescription,
        copy.notifications.dataSources.creation.failure
      );
    }

    case DataSourcesActionType.ADD_DATA_SOURCE_SUCCESS: {
      return queueNotification(prevState, action.type, {
        message: copy.notifications.dataSources.creation.success(
          action.payload.action.dataSource.id
        ),
        intent: Intent.SUCCESS,
      });
    }

    case DataSourcesActionType.REQUEST_SUGGESTIONS_FAILURE: {
      return queueFailureNotification(
        prevState,
        action,
        copy.notifications.dataSources.suggestions.actionDescription,
        copy.notifications.dataSources.suggestions.failure
      );
    }
    case DataSourcesActionType.NO_SUGGESTIONS_FOUND_WARNING: {
      return queueNotification(prevState, action.type, {
        message: copy.notifications.dataSources.suggestions.noneFound,
        intent: Intent.WARNING,
        timeout: 10000,
      });
    }

    case ProjectsActionType.EXPORTED_PROJECT: {
      const { filename } = action.payload;
      if (filename) {
        sendHubspotEvent(TrackingEventList.DESIGNER_EXPORT_PROJECT);
      }
      return queueNotification(
        prevState,
        action.type,
        filename
          ? {
              message: copy.notifications.project.exported(filename),
              intent: Intent.SUCCESS,
              icon: icons.connection,
            }
          : {
              message: copy.notifications.project.exportFailure,
              intent: Intent.DANGER,
              icon: IconNames.ERROR,
            }
      );
    }

    case ProjectsActionType.IMPORTED_INVALID_PROJECT_FROM_FILE: {
      return queueNotification(prevState, action.type, {
        message: copy.notifications.project.importFromFile.invalid,
        intent: Intent.DANGER,
      });
    }
    case ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH_PREVIEW_ERROR: {
      const { message, isWarning } = action.payload;
      return queueNotification(prevState, action.type, {
        message,
        intent: isWarning ? Intent.WARNING : Intent.DANGER,
      });
    }
    case ProjectsActionType.UPLOADED_MISMATCHED_HEADERS_CSV: {
      const { filename } = action.payload;
      return queueNotification(prevState, action.type, {
        message: copy.notifications.csv.mismatchedHeaders(filename),
        intent: Intent.DANGER,
      });
    }
    case ProjectsActionType.UPLOADED_PARTIAL_CSV: {
      return queueNotification(prevState, action.type, {
        message: copy.notifications.csv.partial,
        intent: Intent.WARNING,
      });
    }
    case ProjectsActionType.ARCHIVED_PROJECT: {
      const { id, name, restoreAction } = action.payload;

      const undoAction: NotificationAction = {
        text: copy.notifications.defaults.undo,
        actionButtonTestId: getNotificationActionButtonTestId(
          id,
          copy.notifications.defaults.undo
        ),
        onClickAction: restoreAction,
      };

      // This is to hide the undo action when cloud project
      const data: NotificationMetadata = !restoreAction
        ? {}
        : {
            actions: [undoAction],
            dismissOnAction: true,
          };

      return queueNotification(
        prevState,
        action.type,
        {
          intent: Intent.WARNING,
          icon: icons.project,
          message: copy.notifications.project.removed(name),
          timeout: 3000,
        },
        undefined,
        data
      );
    }
    case ProjectsActionType.RESTORED_LOCAL_PROJECT: {
      const { name } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.SUCCESS,
        icon: icons.project,
        message: copy.notifications.project.restored(name),
      });
    }
    case ProjectsActionType.RESTORED_CLOUD_PROJECT: {
      const { name } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.SUCCESS,
        icon: icons.project,
        message: copy.notifications.project.restored(name),
      });
    }
    case ProjectsActionType.MIGRATED_LOCAL_PROJECT_BACKUP_TO_CLOUD: {
      const { name } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.SUCCESS,
        icon: icons.project,
        message: copy.notifications.project.migratedLocalProjectBackupToCloud(
          name,
          isOnPremise()
        ),
      });
    }
    case ProjectsActionType.MIGRATED_CLOUD_PROJECT_TO_LOCAL: {
      const { name } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.SUCCESS,
        icon: icons.project,
        message: copy.notifications.project.storedLocally(name),
      });
    }
    case ProjectsActionType.MIGRATED_LOCAL_PROJECT_TO_CLOUD: {
      const { name } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.SUCCESS,
        icon: icons.project,
        message: copy.notifications.project.storedOnCloud(name, isOnPremise()),
      });
    }
    case ProjectsActionType.DUPLICATED_CLOUD_BACKUP_TO_LOCAL_PROJECT: {
      const { name } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.SUCCESS,
        icon: icons.project,
        message:
          copy.notifications.project.duplicatedCloudBackupToLocalProject(name),
      });
    }
    case ProjectsActionType.BACKED_UP_CLOUD_PROJECT_TO_LOCAL_PROJECT: {
      const { project } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.WARNING,
        icon: icons.project,
        message: copy.notifications.project.backedUpCloudProjectToLocal(
          project.name
        ),
      });
    }
    case ProjectsActionType.OPENED_MISSING_PROJECT: {
      const { name } = action.payload;
      return queueNotification(prevState, action.type, {
        intent: Intent.WARNING,
        icon: icons.project,
        message: copy.notifications.project.openedMissing(name),
      });
    }
    case ProjectsActionType.PUBLISH_PROJECT_FINISHED: {
      const { name, responses, intent } = action.payload;

      let message;
      if (intent === Intent.WARNING) {
        message = copy.notifications.publishProject.partial(name);
      } else if (intent === Intent.DANGER) {
        message = copy.notifications.publishProject.failure(name);
      } else {
        message = copy.notifications.publishProject.success(name);
      }

      const timeout = intent === Intent.SUCCESS ? undefined : 0;
      const baseState = queueNotification(prevState, action.type, {
        intent,
        icon: icons.project,
        message,
        timeout,
      });

      return responses.reduce<NotificationsState>(
        (state, response) =>
          response.success
            ? queueNotification(state, response.action.type, {
                intent: Intent.SUCCESS,
                icon: icons.project,
                message: response.message,
                timeout,
              })
            : queueFailureNotification(
                state,
                response.action,
                response.actionDescription,
                response.message
              ),

        baseState
      );
    }

    case ProjectsActionType.PUBLISH_UNIQUE_KEY_SETS_FINISHED: {
      const { responses } = action.payload;

      return responses.reduce<NotificationsState>(
        (state, response) =>
          response.success
            ? queueNotification(state, response.action.type, {
                intent: Intent.SUCCESS,
                icon: icons.project,
                message: response.message,
              })
            : queueFailureNotification(
                state,
                response.action,
                response.actionDescription,
                response.message
              ),

        prevState
      );
    }

    case uiSlice.actions.voiceboxFeedbackFailed.type: {
      const { message } = action.payload as Parameters<
        typeof uiSlice.actions.voiceboxFeedbackFailed
      >[0];

      return queueNotification(prevState, action.type, {
        message,
        intent: Intent.DANGER,
      });
    }

    case uiSlice.actions.voiceboxModelingAndMappingFailed.type: {
      const { message } = action.payload as Parameters<
        typeof uiSlice.actions.voiceboxModelingAndMappingFailed
      >[0];

      return queueNotification(prevState, action.type, {
        message,
        intent: Intent.DANGER,
      });
    }

    default:
      return prevState;
  }
};
